/** @format */

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import { FormikProps } from "formik";
import React from "react";
import { ApplicantInitialState } from "../../Models";
import GuardianType from "../../../../common/dropdowns/GuardianType";
import GuardianTypeOptions from "../../../../common/dropdowns/GuardianTypeOptions";
import EmploymentStatus from "../../../../common/dropdowns/EmploymentStatus";
import BusinessType from "../../../../common/dropdowns/businessType";


interface Props {
  formikAddGuardian: FormikProps<any>;
  formik: FormikProps<any>;
  applicant: ApplicantInitialState;
  // True when an existing guardian's values are loaded into the inline form
  // for amendment. Switches the primary button from "Add" to "Update" and
  // shows a Cancel button, and lets the user submit even when guardians.length
  // already equals the 2-guardian cap (because we're replacing in place).
  isEditing?: boolean;
  onCancelEdit?: () => void;
}

export default function GuardianForm({
  formikAddGuardian,
  applicant,
  formik,
  isEditing,
  onCancelEdit,
}: Props) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          id="firstName"
          name="firstName"
          label="Enter First name(s)"
          value={formikAddGuardian.values.firstName}
          onChange={formikAddGuardian.handleChange}
          onBlur={formikAddGuardian.handleBlur}
          type="text"
          error={
            formikAddGuardian.touched.firstName &&
            Boolean(formikAddGuardian.errors.firstName)
          }
          helperText={
            formikAddGuardian.touched.firstName &&
            (formikAddGuardian.errors.firstName as React.ReactNode)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          id="surname"
          name="surname"
          label="Enter Surname"
          value={formikAddGuardian.values.surname}
          onChange={formikAddGuardian.handleChange}
          onBlur={formikAddGuardian.handleBlur}
          type="text"
          error={
            formikAddGuardian.touched.surname &&
            Boolean(formikAddGuardian.errors.surname)
          }
          helperText={
            formikAddGuardian.touched.surname &&
            (formikAddGuardian.errors.surname as React.ReactNode)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          id="identityNumber"
          name="identityNumber"
          label="Enter Identity Number"
          value={formikAddGuardian.values.identityNumber}
          onChange={formikAddGuardian.handleChange}
          onBlur={formikAddGuardian.handleBlur}
          type="text"
          error={
            formikAddGuardian.touched.identityNumber &&
            Boolean(formikAddGuardian.errors.identityNumber)
          }
          helperText={
            formikAddGuardian.touched.identityNumber &&
            (formikAddGuardian.errors.identityNumber as React.ReactNode)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          id="contactNumber"
          name="contactNumber"
          label="Enter Contact Number"
          value={formikAddGuardian.values.contactNumber}
          onChange={formikAddGuardian.handleChange}
          onBlur={formikAddGuardian.handleBlur}
          type="text"
          error={
            formikAddGuardian.touched.contactNumber &&
            Boolean(formikAddGuardian.errors.contactNumber)
          }
          helperText={
            formikAddGuardian.touched.contactNumber &&
            (formikAddGuardian.errors.contactNumber as React.ReactNode)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <GuardianType formik={formikAddGuardian} />
      </Grid>

      {formikAddGuardian.values.guardianTypeId > 0 && (
        <Grid item xs={12} md={6}>
          <GuardianTypeOptions
            formik={formikAddGuardian}
            disableParents={false}
          />
        </Grid>
      )}
      {/* <Grid item xs={12}></Grid> */}

      <Grid item xs={12} md={6}>
        <EmploymentStatus formik={formikAddGuardian} isDisabled={false} />
      </Grid>

      {formikAddGuardian.values.employmentStatus !== -1 && (
        <Grid item xs={12} md={6}>
          <BusinessType formik={formikAddGuardian} isDisabled={false} />
        </Grid>
      )}

      {formikAddGuardian.values.businessType === 12 && (
        <>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id="bus"
              name="businessName"
              label="Enter Business Name"
              value={formikAddGuardian.values.businessName}
              onChange={formikAddGuardian.handleChange}
              onBlur={formikAddGuardian.handleBlur}
              type="text"
              error={
                formikAddGuardian.touched.businessName &&
                Boolean(formikAddGuardian.errors.businessName)
              }
              helperText={
                formikAddGuardian.touched.businessName &&
                (formikAddGuardian.errors.businessName as React.ReactNode)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id=""
              name="cipcRegistrationNumber"
              label="CIPC Registration Number"
              value={formikAddGuardian.values.cipcRegistrationNumber}
              onChange={formikAddGuardian.handleChange}
              onBlur={formikAddGuardian.handleBlur}
              type="text"
              error={
                formikAddGuardian.touched.cipcRegistrationNumber &&
                Boolean(formikAddGuardian.errors.cipcRegistrationNumber)
              }
              helperText={
                formikAddGuardian.touched.cipcRegistrationNumber &&
                (formikAddGuardian.errors
                  .cipcRegistrationNumber as React.ReactNode)
              }
            />
          </Grid>
        </>
      )}

      {formikAddGuardian.values.employed && (
        <>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id="occupation"
              name="occupation"
              label="Enter Occupation"
              value={formikAddGuardian.values.occupation}
              onChange={formikAddGuardian.handleChange}
              onBlur={formikAddGuardian.handleBlur}
              type="text"
              error={
                formikAddGuardian.touched.occupation &&
                Boolean(formikAddGuardian.errors.occupation)
              }
              helperText={
                formikAddGuardian.touched.occupation &&
                (formikAddGuardian.errors.occupation as React.ReactNode)
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id="companyName"
              name="companyName"
              label="Enter Company Name"
              value={formikAddGuardian.values.companyName}
              onChange={formikAddGuardian.handleChange}
              onBlur={formikAddGuardian.handleBlur}
              type="text"
              error={
                formikAddGuardian.touched.companyName &&
                Boolean(formikAddGuardian.errors.companyName)
              }
              helperText={
                formikAddGuardian.touched.companyName &&
                (formikAddGuardian.errors.companyName as React.ReactNode)
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id="companyContact"
              name="companyContact"
              label="Enter Employer contact details"
              value={formikAddGuardian.values.companyContact}
              onChange={formikAddGuardian.handleChange}
              onBlur={formikAddGuardian.handleBlur}
              type="text"
              error={
                formikAddGuardian.touched.companyContact &&
                Boolean(formikAddGuardian.errors.companyContact)
              }
              helperText={
                formikAddGuardian.touched.companyContact &&
                (formikAddGuardian.errors.companyContact as React.ReactNode)
              }
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <FormControl>
          <FormLabel id="demo-controlled-radio-buttons-group">
            {`Is ${formikAddGuardian.values.firstName} a household contributor?`}
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="doTheyContribute"
            value={formikAddGuardian.values.doTheyContribute}
            onChange={(e) => {
              if (e.target.value === "false")
                formikAddGuardian.setFieldValue("doTheyContribute", false);
              else if (e.target.value === "true")
                formikAddGuardian.setFieldValue("doTheyContribute", true);
            }}
          >
            <FormControlLabel value={true} control={<Radio />} label="Yes" />
            <FormControlLabel value={false} control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Box className="error_material">
          {formikAddGuardian.touched.dependants &&
            (formikAddGuardian.errors.dependants as React.ReactNode)}
        </Box>

        {formik.values.guardians.length === 2 && !isEditing && (
          <Grid item xs={12} mb={2}>
            <Alert variant="standard" color="success" severity="success">
              All required Guardians/Parents have been added successfully. You
              can Press Save & Continue
            </Alert>
          </Grid>
        )}
        {isEditing && (
          <Grid item xs={12} mb={2}>
            <Alert variant="standard" color="warning" severity="warning">
              You are editing an existing guardian. Click <b>Update Guardian</b>{" "}
              to save your changes or <b>Cancel</b> to discard them.
            </Alert>
          </Grid>
        )}
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            // While editing we always allow the submit (it's an in-place
            // replace, not an append, so the 2-guardian cap doesn't apply).
            disabled={!isEditing && formik.values.guardians.length === 2}
            color={isEditing ? "warning" : "info"}
            onClick={() => formikAddGuardian.submitForm()}
            startIcon={
              formikAddGuardian.isSubmitting ? (
                <CircularProgress
                  style={{ width: 15, height: 15, color: "#fff" }}
                />
              ) : isEditing ? (
                <EditIcon />
              ) : (
                <AddIcon />
              )
            }
          >
            {isEditing ? "Update Guardian" : "Add Guardian or Parent"}
          </Button>
          {isEditing && onCancelEdit && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
          )}
        </Stack>

        <FormHelperText sx={{ color: "green", fontSize: "14px" }}>
          * Please ensure you use the 'Add Guardian or Parent' button to add all
          your Guardians or Parents before you press Save & Continue. You can
          add up to two guardians. Use the pencil icon on a guardian card to
          edit an existing guardian.
        </FormHelperText>
      </Grid>
    </Grid>
  );
}
